import { Card } from '@lms/ui/card';
import { cn } from '@lms/ui/utils';
import { ChartNoAxesColumn } from 'lucide-react';
import type * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatNumber } from '../format';
import { type PivotRow, PREVIOUS_COLOR, type Series } from '../series';

/** Space between touching marks (stacked segments), in px. */
const GAP = 2;
/** Corner radius on the free end of a bar, in px. */
const RADIUS = 4;
/** Bars never grow thicker than this, whatever the slot width. */
export const MAX_BAR_SIZE = 24;

/**
 * Card wrapper shared by every dashboard chart: title and description on the
 * left, an optional headline value on the right, and the chart filling the
 * rest so cards in the same row line up.
 */
export function ChartCard({
  title,
  description,
  value,
  action,
  center,
  className,
  children,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  value?: React.ReactNode;
  /** Centred in the header row, e.g. a view switch. */
  center?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn('flex min-w-0 flex-col', className)}>
      <div
        className={cn(
          'flex items-start justify-between gap-4 p-5 pb-4',
          // Equal side columns keep the centre slot centred in the card.
          center &&
            'grid grid-cols-[1fr_auto] items-center sm:grid-cols-[1fr_auto_1fr]',
        )}
      >
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm leading-none font-medium">{title}</h3>
          {description && (
            <p className="text-muted-foreground text-xs">{description}</p>
          )}
        </div>
        {center && (
          <div className="col-span-2 row-start-2 justify-self-center sm:col-span-1 sm:col-start-2 sm:row-start-1">
            {center}
          </div>
        )}
        {(value !== undefined || action) && (
          <div className="flex items-start justify-end gap-4 justify-self-end">
            {value !== undefined && (
              <div className="shrink-0 text-2xl leading-none font-semibold tracking-tight">
                {value}
              </div>
            )}
            {action}
          </div>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col justify-center px-5 pb-5">
        {children}
      </div>
    </Card>
  );
}

/** Placeholder with the same footprint as the chart it replaces. */
export function ChartEmpty({
  label,
  height,
}: {
  label: React.ReactNode;
  height: number;
}) {
  return (
    <div
      className="text-muted-foreground flex flex-col items-center justify-center gap-2 rounded-md border border-dashed text-sm"
      style={{ height }}
    >
      <ChartNoAxesColumn className="size-5 opacity-60" aria-hidden />
      {label}
    </div>
  );
}

export function Swatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className="size-2.5 shrink-0 rounded-[3px]"
      style={{ backgroundColor: color }}
    />
  );
}

/** HTML legend (wraps instead of overflowing, unlike the SVG one). */
export function ChartLegendList({
  series,
  comparisonLabel,
  comparisonColor = PREVIOUS_COLOR,
  always,
  className,
}: {
  series: readonly Series[];
  /** Adds a key for a last-year comparison, in `comparisonColor`. */
  comparisonLabel?: string;
  comparisonColor?: string;
  /** Show even a single series (when nothing else on the card names it). */
  always?: boolean;
  className?: string;
}) {
  if (series.length === 0) return null;
  if (series.length < 2 && !comparisonLabel && !always) return null;
  return (
    <ul
      className={cn(
        'text-muted-foreground flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-xs',
        className,
      )}
    >
      {series.map((s) => (
        <li key={s.key} className="flex min-w-0 items-center gap-1.5">
          <Swatch color={s.color} />
          <span className="truncate" title={s.name}>
            {s.name}
          </span>
        </li>
      ))}
      {comparisonLabel && (
        <li className="flex min-w-0 items-center gap-1.5">
          <Swatch color={comparisonColor} />
          <span className="truncate">{comparisonLabel}</span>
        </li>
      )}
    </ul>
  );
}

/** Distance between the pointer and the tooltip, in px. */
const TOOLTIP_OFFSET = 12;
/** Minimum distance between the tooltip and the viewport edge, in px. */
const VIEWPORT_MARGIN = 8;

/**
 * Renders Recharts tooltip content in a portal on `document.body`, so it can
 * spill outside the chart (and its card) while staying inside the viewport:
 * it sits beside the active point and flips to the other side, then clamps,
 * when it would cross an edge. `coordinate` is the one Recharts passes to
 * tooltip content (relative to the chart wrapper).
 */
export function FloatingTooltip({
  coordinate,
  children,
}: {
  coordinate?: { x?: number; y?: number };
  children: React.ReactNode;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // No deps: re-place after every render, as size and coordinate change together.
  useLayoutEffect(() => {
    const chart = anchor.current?.closest('.recharts-wrapper');
    const el = box.current;
    if (!chart || !el || coordinate?.x == null || coordinate.y == null) return;
    const rect = chart.getBoundingClientRect();
    const x = rect.left + coordinate.x;
    const y = rect.top + coordinate.y;
    const place = (at: number, size: number, viewport: number) => {
      let start = at + TOOLTIP_OFFSET;
      if (start + size > viewport - VIEWPORT_MARGIN) {
        start = at - TOOLTIP_OFFSET - size;
      }
      return Math.max(
        VIEWPORT_MARGIN,
        Math.min(start, viewport - VIEWPORT_MARGIN - size),
      );
    };
    const root = document.documentElement;
    const left = place(x, el.offsetWidth, root.clientWidth);
    const top = place(y, el.offsetHeight, root.clientHeight);
    setPos((prev) =>
      prev?.left === left && prev.top === top ? prev : { left, top },
    );
  });

  return (
    <span ref={anchor} hidden>
      {createPortal(
        <div
          ref={box}
          className="pointer-events-none fixed z-50 max-w-[calc(100vw-16px)]"
          style={{
            left: pos?.left ?? 0,
            top: pos?.top ?? 0,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </span>
  );
}

/**
 * Tooltip for stacked charts: the category, every non-zero series (largest
 * first) and the total. Reads the pivoted row rather than Recharts' payload
 * so zero series are skipped and the order is stable.
 */
export function SeriesTooltip({
  active,
  payload,
  coordinate,
  series,
  title,
  locale,
  totalLabel,
  valueFormatter,
  seriesColor,
  footer,
  alwaysNamed,
}: {
  active?: boolean;
  payload?: { payload?: PivotRow }[];
  coordinate?: { x?: number; y?: number };
  series: readonly Series[];
  title: (row: PivotRow) => React.ReactNode;
  locale?: string;
  totalLabel?: string;
  valueFormatter?: (value: number) => string;
  seriesColor?: (series: Series, row: PivotRow) => string;
  /** Extra line under the total (label and value). */
  footer?: (row: PivotRow) => React.ReactNode;
  /** Name a single series too (when nothing else on the card names it). */
  alwaysNamed?: boolean;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const fmt = valueFormatter ?? ((v: number) => formatNumber(v, locale));
  const items = series
    .map((s) => ({ s, value: Number(row[s.key] ?? 0) }))
    .filter(({ value }) => value > 0)
    .sort((a, b) => b.value - a.value);

  if (series.length === 1 && !alwaysNamed) {
    return (
      <FloatingTooltip coordinate={coordinate}>
        <div className="bg-background border-border/60 grid min-w-32 max-w-72 grid-cols-1 gap-1 rounded-lg border px-3 py-2 text-xs shadow-lg">
          <div className="text-foreground font-medium break-words">
            {title(row)}
          </div>
          <div className="text-foreground font-semibold tabular-nums">
            {fmt(row.total)}
          </div>
          {footer && (
            <div className="flex items-center justify-between gap-2">
              {footer(row)}
            </div>
          )}
        </div>
      </FloatingTooltip>
    );
  }

  return (
    <FloatingTooltip coordinate={coordinate}>
      <div className="bg-background border-border/60 grid max-w-72 min-w-40 grid-cols-1 gap-1.5 rounded-lg border px-3 py-2 text-xs shadow-lg">
        <div className="text-foreground font-medium break-words">
          {title(row)}
        </div>
        {items.map(({ s, value }) => (
          <div key={s.key} className="flex items-center gap-2">
            <Swatch color={seriesColor?.(s, row) ?? s.color} />
            <span className="text-muted-foreground min-w-0 flex-1 truncate">
              {s.name}
            </span>
            <span className="text-foreground font-medium tabular-nums">
              {fmt(value)}
            </span>
          </div>
        ))}
        {items.length !== 1 && (
          <div className="border-border/60 flex items-center justify-between gap-2 border-t pt-1.5">
            <span className="text-muted-foreground">
              {totalLabel ?? 'Total'}
            </span>
            <span className="text-foreground font-semibold tabular-nums">
              {fmt(row.total)}
            </span>
          </div>
        )}
        {footer && (
          <div className="flex items-center justify-between gap-2">
            {footer(row)}
          </div>
        )}
      </div>
    </FloatingTooltip>
  );
}

function roundedPath(
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  end: 'top' | 'right',
): string {
  if (r <= 0) return `M${x},${y}h${w}v${h}h${-w}Z`;
  if (end === 'top') {
    return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
  }
  return `M${x},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h - r}Q${x + w},${y + h} ${x + w - r},${y + h}H${x}Z`;
}

interface SegmentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: PivotRow;
}

/**
 * Bar shape for stacked series: rounds only the outermost non-zero segment
 * and leaves a small gap between segments instead of drawing strokes.
 */
export function segmentShape(
  seriesKey: string,
  keys: readonly string[],
  orientation: 'column' | 'bar',
  fillFor?: (row: PivotRow) => string,
) {
  return function Segment({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill,
    payload,
  }: SegmentProps) {
    if (width <= 0 || height <= 0 || !payload) return <g />;
    const present = keys.filter((k) => Number(payload[k] ?? 0) > 0);
    const isBase = present[0] === seriesKey;
    const isEnd = present[present.length - 1] === seriesKey;
    const color = fillFor?.(payload) ?? fill;

    if (orientation === 'column') {
      const gap = isBase ? 0 : Math.min(GAP, height / 2);
      const h = height - gap;
      const r = isEnd ? Math.min(RADIUS, width / 2, h) : 0;
      return <path d={roundedPath(x, y, width, h, r, 'top')} fill={color} />;
    }
    const gap = isBase ? 0 : Math.min(GAP, width / 2);
    const w = width - gap;
    const r = isEnd ? Math.min(RADIUS, height / 2, w) : 0;
    return (
      <path d={roundedPath(x + gap, y, w, height, r, 'right')} fill={color} />
    );
  };
}

/** Shared axis styling: muted ticks, no axis lines, hairline grid. */
export const axisTick = { fontSize: 11, fill: 'var(--muted-foreground)' };

/**
 * Category-axis tick that truncates to fit instead of wrapping (Recharts'
 * default tick breaks long names onto several cramped lines).
 */
export function truncatedTick(width: number) {
  const maxChars = Math.max(Math.floor((width - 8) / 7), 4);
  return function Tick({
    x = 0,
    y = 0,
    payload,
  }: {
    x?: number;
    y?: number;
    payload?: { value: string };
  }) {
    const label = String(payload?.value ?? '');
    const short =
      label.length > maxChars
        ? `${label.slice(0, maxChars - 1).trimEnd()}…`
        : label;
    return (
      <text
        x={x - 8}
        y={y}
        textAnchor="end"
        dominantBaseline="central"
        fontSize={12}
        fill="var(--foreground)"
      >
        <title>{label}</title>
        {short}
      </text>
    );
  };
}

/** Value label drawn just past the end of a horizontal bar, never wrapped. */
export function barEndLabel(format: (v: number) => string) {
  return function EndLabel(props: {
    x?: number | string;
    y?: number | string;
    width?: number | string;
    height?: number | string;
    value?: number | string;
  }) {
    const x = Number(props.x ?? 0) + Number(props.width ?? 0) + 8;
    const y = Number(props.y ?? 0) + Number(props.height ?? 0) / 2;
    return (
      <text
        x={x}
        y={y}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
        fill="var(--foreground)"
      >
        {format(Number(props.value ?? 0))}
      </text>
    );
  };
}
